import DateObject from 'react-date-object';
import gregorian from 'react-date-object/calendars/gregorian';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { normalizeDigits } from './phone';

const GREGORIAN_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const JALALI_DATE_RE = /^\d{4}\/\d{1,2}\/\d{1,2}/;
const DISPLAY_TZ = 'Asia/Tehran';

/** DateObject واقعی یا شبیه (مثلاً اگر دو نسخه از پکیج لود شده باشد) */
function isDateObjectLike(value) {
  return (
    value instanceof DateObject
    || (value != null
      && typeof value === 'object'
      && typeof value.convert === 'function'
      && typeof value.format === 'function'
      && value.calendar != null)
  );
}

/** آیا مقدار API شامل بخش زمان است (نه فقط تاریخ خالص)؟ */
function sourceHasTimeComponent(raw) {
  const text = normalizeDigits(raw).trim();
  if (!text) return false;
  if (text.includes('T')) return true;
  if (/\d{1,2}:\d{2}/.test(text)) return true;
  return false;
}

function tehranDateObjectFromJsDate(jsDate) {
  if (!(jsDate instanceof Date) || Number.isNaN(jsDate.getTime())) return null;
  const tehranParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: DISPLAY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(jsDate);
  const part = (type) => tehranParts.find((p) => p.type === type)?.value;
  const gregorianObj = new DateObject({
    year: Number(part('year')),
    month: Number(part('month')),
    day: Number(part('day')),
    hour: Number(part('hour')),
    minute: Number(part('minute')),
    second: Number(part('second')),
    calendar: gregorian,
  });
  return gregorianObj.convert(persian, persian_fa);
}

function hasExplicitTimezone(text) {
  return /(?:Z|[+-]\d{2}:?\d{2})(?:\.\d+)?$/i.test(text.trim()) || /[+-]\d{2}:\d{2}/.test(text);
}

function fromTimestamp(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return null;
  try {
    return tehranDateObjectFromJsDate(new Date(Number(seconds) * 1000));
  } catch {
    return null;
  }
}

function fromGregorianDateTime(text) {
  try {
    if (hasExplicitTimezone(text)) {
      const normalized = text.includes('T') ? text : text.replace(' ', 'T');
      return tehranDateObjectFromJsDate(new Date(normalized));
    }

    const normalized = text.includes('T') ? text : text.replace(' ', 'T');
    const [datePart, timePartRaw = ''] = normalized.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const timePart = timePartRaw.replace(/[+Z].*$/i, '');
    const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
    const gregorianObj = new DateObject({
      year,
      month,
      day,
      hour: hour || 0,
      minute: minute || 0,
      second: Math.floor(second || 0),
      calendar: gregorian,
    });
    return gregorianObj.convert(persian, persian_fa);
  } catch {
    return null;
  }
}

/** DateObject از PersianDatePicker یا تبدیل تقویم — بدون stringify که ارقام فارسی را خراب کند */
function fromDateObject(value) {
  if (!isDateObjectLike(value) || value.isValid === false) return null;
  try {
    if (value.calendar?.name === 'persian') {
      if (value.locale?.name === persian_fa.name) return value;
      return value.convert(persian, persian_fa);
    }
    return value.convert(persian, persian_fa);
  } catch {
    return null;
  }
}

export function toJalaliDateObject(value) {
  if (value == null || value === '') return null;
  try {
    // PersianDatePicker / react-date-object — قبل از timestamp و String()
    const fromPicker = fromDateObject(value);
    if (fromPicker) return fromPicker;

    // آبجکت‌های API با unix seconds (نه DateObject)
    if (typeof value === 'object' && !isDateObjectLike(value) && value.timestamp != null) {
      const fromTs = fromTimestamp(value.timestamp);
      if (fromTs) return fromTs;
    }

    const raw = typeof value === 'object' ? value.jalali || value.iso || value : value;
    if (isDateObjectLike(raw)) {
      return fromDateObject(raw);
    }
    const text = normalizeDigits(raw).trim();
    if (!text) return null;

    if (JALALI_DATE_RE.test(text)) {
      const [datePart, timePart] = text.split(/\s+/);
      const [y, m, d] = datePart.split('/').map(Number);
      const config = { year: y, month: m, day: d, calendar: persian, locale: persian_fa };
      if (timePart) {
        const [hh, mm, ss] = timePart.split(':').map(Number);
        Object.assign(config, { hour: hh || 0, minute: mm || 0, second: ss || 0 });
      }
      return new DateObject(config);
    }

    if (GREGORIAN_DATE_RE.test(text) || text.includes('T')) {
      const parsed = fromGregorianDateTime(text);
      if (parsed) return parsed;
      return new DateObject({ date: text, calendar: gregorian }).convert(persian, persian_fa);
    }

    return new DateObject({ date: text, calendar: gregorian }).convert(persian, persian_fa);
  } catch {
    return null;
  }
}

/**
 * جدا کردن تاریخ و ساعت از مقدار API — نمایش همیشه شمسی
 */
export function splitApiDateTime(value, { alwaysShowTime = false } = {}) {
  if (value == null || value === '') return { date: null, time: null };

  const rawSource =
    typeof value === 'object' ? value.iso || value.jalali || '' : String(value);

  const jalaliObj = toJalaliDateObject(value);
  if (jalaliObj) {
    const showTime =
      alwaysShowTime
      || (typeof value === 'object' && value.timestamp != null)
      || sourceHasTimeComponent(rawSource)
      || (typeof value === 'object' && typeof value.jalali === 'string' && /\d{1,2}:\d{2}/.test(value.jalali));
    return {
      date: jalaliObj.format('YYYY/MM/DD'),
      time: showTime ? jalaliObj.format('HH:mm') : null,
    };
  }

  const raw = rawSource.trim();
  if (!raw) return { date: null, time: null };

  if (raw.includes(' ')) {
    const [date, ...rest] = raw.split(/\s+/);
    const timePart = rest.join(' ') || null;
    return { date, time: timePart ? timePart.slice(0, 5) : null };
  }

  if (raw.includes('T')) {
    const [date, timePart] = raw.split('T');
    const time = timePart?.replace(/[+Z].*$/, '').slice(0, 5) || null;
    return { date, time: alwaysShowTime ? time : time };
  }

  return { date: raw, time: null };
}

/** یک خط تاریخ شمسی (با ساعت اختیاری) */
export function formatJalaliDateTime(value, fallback = '—') {
  const { date, time } = splitApiDateTime(value);
  if (!date) return fallback;
  return time ? `${date} ${time}` : date;
}

/**
 * بازه شروع → پایان برای چیپ‌ها و خلاصه.
 * اگر هر دو در یک روز باشند تاریخ تکرار نمی‌شود تا ساعت پایان دیده شود.
 */
export function formatMeetingScheduleRange(startsAt, endsAt, fallback = 'انتخاب نشده') {
  const start = splitApiDateTime(startsAt);
  const end = splitApiDateTime(endsAt);
  if (!start.date) return fallback;

  const startText = start.time ? `${start.date} ${start.time}` : start.date;
  if (!end.date) return startText;

  if (start.date === end.date && start.time && end.time) {
    return `${start.date} ${start.time} ← ${end.time}`;
  }

  const endText = end.time ? `${end.date} ${end.time}` : end.date;
  return `${startText} ← ${endText}`;
}

/** فقط تاریخ شمسی */
export function formatJalaliDate(value, fallback = '—') {
  const { date } = splitApiDateTime(value);
  return date || fallback;
}

function toDisplayIso(value) {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000;
    return new Date(ms).toISOString();
  }
  return value;
}

/**
 * Instant برای مقایسهٔ تاریخ API (ISO، جلالی، یا {iso, jalali, timestamp}).
 * `new Date('1405/06/30')` را سال میلادی ۱۴۰۵ فرض می‌کند و سهمیهٔ جاری را منقضی نشان می‌دهد.
 */
export function apiDateToMs(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  if (isDateObjectLike(value)) {
    try {
      const date = value.toDate?.();
      if (date instanceof Date && !Number.isNaN(date.getTime())) return date.getTime();
    } catch {
      return null;
    }
    return null;
  }
  if (typeof value === 'object') {
    if (value.timestamp != null && Number.isFinite(Number(value.timestamp))) {
      return apiDateToMs(Number(value.timestamp));
    }
    if (value.iso) return apiDateToMs(value.iso);
    if (value.jalali) return apiDateToMs(value.jalali);
    return null;
  }
  const text = normalizeDigits(String(value)).trim();
  if (!text) return null;
  if (JALALI_DATE_RE.test(text)) {
    const jalali = toJalaliDateObject(text);
    try {
      const date = jalali?.toDate?.();
      if (date instanceof Date && !Number.isNaN(date.getTime())) return date.getTime();
    } catch {
      return null;
    }
    return null;
  }
  const iso = text.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : parsed;
}

/** زمان نسبی ضبط: «امروز، ۱۳:۴۵» یا تاریخ جلالی + ساعت */
export function formatRelativeRecordingWhen(value, fallback = '—') {
  const jalali = toJalaliDateObject(toDisplayIso(value));
  if (!jalali) return fallback;
  const time = jalali.format('HH:mm');
  const today = toJalaliDateObject(new Date().toISOString());
  if (today && jalali.format('YYYY/MM/DD') === today.format('YYYY/MM/DD')) {
    return `امروز، ${time}`;
  }
  return `${jalali.format('YYYY/MM/DD')}، ${time}`;
}

/**
 * پیام انقضای OTP برای کاربر: زمان شمسی تهران، بدون ISO خام و بدون «طبق سرور».
 * امروز → «کد تا امروز، ساعت ۱۵:۴۹ معتبر است.»
 * روز دیگر → «کد تا ۱۴۰۵/۰۶/۳۰، ساعت ۱۵:۴۹ معتبر است.»
 */
export function formatOtpExpiryHint(expiresAt, fallback = 'کد تأیید ارسال شد.') {
  const jalali = toJalaliDateObject(toDisplayIso(expiresAt) ?? expiresAt);
  if (!jalali) return fallback;
  const time = jalali.format('HH:mm');
  const today = toJalaliDateObject(new Date().toISOString());
  if (today && jalali.format('YYYY/MM/DD') === today.format('YYYY/MM/DD')) {
    return `کد تا امروز، ساعت ${time} معتبر است.`;
  }
  return `کد تا ${jalali.format('YYYY/MM/DD')}، ساعت ${time} معتبر است.`;
}
